/*  CourseDashboard — Screen 1 of the Certification Author flow.
    ────────────────────────────────────────────────────────────
    Lists every course this author owns (or every course, if admin).
    Tabs: All / Draft / Under Review / Published / Rejected.
    "New Course" CTA jumps to the setup screen.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Plus, MagnifyingGlass, PencilSimple, Eye, Trash, Books, ClockCounterClockwise,
    CheckCircle, XCircle, WarningCircle, ArrowRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { promptDialog } from "@/lib/confirm";


const TABS = [
    { id: "all",           label: "All",           icon: Books },
    { id: "draft",         label: "Draft",         icon: PencilSimple },
    { id: "under_review",  label: "Under Review",  icon: ClockCounterClockwise },
    { id: "published",     label: "Published",     icon: CheckCircle },
    { id: "rejected",      label: "Rejected",      icon: XCircle },
];

const STATUS_META = {
    draft:        { label: "Draft",        cls: "bg-slate-100 text-slate-700 border-slate-200",       Icon: PencilSimple },
    under_review: { label: "Under Review", cls: "bg-amber-50 text-amber-800 border-amber-200",         Icon: ClockCounterClockwise },
    published:    { label: "Published",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200",   Icon: CheckCircle },
    rejected:     { label: "Rejected",     cls: "bg-rose-50 text-rose-800 border-rose-200",            Icon: XCircle },
};


export default function CourseDashboard() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    const [q, setQ] = useState("");
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/course-builder/courses");
            setCourses(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load courses.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const prev = document.title;
        document.title = "Certification Author — Invisique";
        return () => { document.title = prev; };
    }, []);

    const filtered = useMemo(() => {
        let list = courses;
        if (tab !== "all") list = list.filter((c) => c.status === tab);
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            list = list.filter((c) =>
                (c.title || "").toLowerCase().includes(needle) ||
                (c.track || "").toLowerCase().includes(needle) ||
                (c.phase || "").toLowerCase().includes(needle),
            );
        }
        return list;
    }, [courses, tab, q]);

    const counts = useMemo(() => {
        const c = { all: courses.length, draft: 0, under_review: 0, published: 0, rejected: 0 };
        courses.forEach((row) => { if (c[row.status] !== undefined) c[row.status] += 1; });
        return c;
    }, [courses]);

    const onDelete = async (course) => {
        const reason = await promptDialog({
            title: `Delete "${course.title}"?`,
            message: "This will permanently remove the course, its modules, lessons, and quizzes. Deletions are logged in the course activity trail — please explain why.",
            placeholder: "e.g. Duplicate of another course · outdated content · replaced by v2…",
            confirmText: "Delete permanently",
            cancelText: "Keep course",
            tone: "danger",
            multiline: true,
            minLength: 10,
            maxLength: 500,
            required: true,
            testid: "course-delete-prompt",
        });
        if (reason == null) return;   // user cancelled
        try {
            await api.delete(`/course-builder/courses/${course.id}`, { data: { reason: reason.trim() } });
            toast.success("Course deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete course.");
        }
    };

    return (
        <div className="px-6 md:px-8 py-8" data-testid="creator-dashboard">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Certification Author</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5">My Courses</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Draft, edit, and submit courses for admin review. Approved courses are published to the doctor's Learning Academy.
                    </p>
                </div>
                <Button onClick={() => navigate("/creator/courses/new")} data-testid="creator-dashboard-new-btn">
                    <Plus size={14} weight="bold" className="mr-1.5" /> New Course
                </Button>
            </div>

            {/* Filter tabs */}
            <div className="mt-6 border-b border-border flex items-center gap-1 overflow-x-auto" data-testid="creator-dashboard-tabs">
                {TABS.map((t) => {
                    const active = tab === t.id;
                    const count = counts[t.id] || 0;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            data-testid={`creator-tab-${t.id}`}
                            className={`relative px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap -mb-px border-b-2 inline-flex items-center gap-1.5 ${
                                active
                                    ? "text-primary border-primary"
                                    : "text-muted-foreground border-transparent hover:text-foreground"
                            }`}
                        >
                            <t.icon size={13} weight={active ? "fill" : "duotone"} />
                            {t.label}
                            <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded ${active ? "bg-primary/10" : "bg-secondary text-muted-foreground"}`}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="mt-4 relative max-w-md">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by title, track, phase…"
                    className="pl-9 h-9"
                    data-testid="creator-dashboard-search"
                />
            </div>

            {/* List */}
            <div className="mt-4">
                {loading ? (
                    <div className="py-12 text-center text-sm text-muted-foreground">Loading courses…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState onNew={() => navigate("/creator/courses/new")} tab={tab} search={q} />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="creator-course-grid">
                        {filtered.map((c) => <CourseCard key={c.id} course={c} onDelete={onDelete} />)}
                    </div>
                )}
            </div>
        </div>
    );
}


function CourseCard({ course, onDelete }) {
    const meta = STATUS_META[course.status] || STATUS_META.draft;
    const canEdit = course.status === "draft" || course.status === "rejected";
    const StatusIcon = meta.Icon;
    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow group" data-testid={`course-card-${course.id}`}>
            {/* Thumbnail band */}
            <div className="relative h-32 bg-gradient-to-br from-primary/10 to-primary/5 border-b border-border flex items-center justify-center">
                <Books size={36} weight="duotone" className="text-primary/40" />
                <span className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
                    <StatusIcon size={10} weight="fill" /> {meta.label}
                </span>
            </div>

            {/* Body */}
            <div className="p-4 space-y-2">
                <h3 className="font-display text-[15px] font-bold leading-snug line-clamp-2 min-h-[2.4em]" data-testid={`course-card-title-${course.id}`}>
                    {course.title}
                </h3>
                {course.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2.2em]">{course.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {course.track && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.track}</span>}
                    {course.phase && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.phase}</span>}
                    {course.duration_minutes ? <span className="px-1.5 py-0.5 rounded bg-secondary">{course.duration_minutes} min</span> : null}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                    <Link
                        to={`/creator/courses/${course.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-bold no-underline hover:bg-primary/90 transition-colors flex-1 justify-center"
                        data-testid={`course-card-open-${course.id}`}
                    >
                        {canEdit ? <><PencilSimple size={11} weight="fill" /> Continue Editing</> : <><Eye size={11} weight="fill" /> View</>}
                        <ArrowRight size={11} weight="bold" />
                    </Link>
                    {course.status !== "published" && (
                        <button
                            type="button"
                            onClick={() => onDelete(course)}
                            className="h-8 w-8 grid place-items-center rounded-md bg-secondary text-muted-foreground hover:text-rose-600 hover:bg-rose-50 border border-border transition-colors"
                            data-testid={`course-card-delete-${course.id}`}
                            title="Delete course"
                        >
                            <Trash size={12} weight="duotone" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


function EmptyState({ onNew, tab, search }) {
    if (search) {
        return (
            <div className="py-14 text-center border border-dashed border-border rounded-2xl">
                <MagnifyingGlass size={28} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
                <div className="font-semibold">No courses match your search.</div>
                <p className="text-sm text-muted-foreground mt-1">Try clearing the search or picking a different tab.</p>
            </div>
        );
    }
    if (tab === "all") {
        return (
            <div className="py-16 text-center border border-dashed border-border rounded-2xl" data-testid="creator-empty-state">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mx-auto mb-3 grid place-items-center">
                    <Books size={26} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold">No courses yet</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Create your first certification course. You can save drafts, add modules and lessons, and submit for admin review when ready.
                </p>
                <Button onClick={onNew} className="mt-4">
                    <Plus size={13} weight="bold" className="mr-1.5" /> Create your first course
                </Button>
            </div>
        );
    }
    return (
        <div className="py-14 text-center border border-dashed border-border rounded-2xl">
            <WarningCircle size={28} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
            <div className="font-semibold">No courses in this bucket yet.</div>
        </div>
    );
}
